import { getFileExtension } from "../../utils/common";
import { DOC_FILES_FIELD_ID, DOC_PLACEMENT_SORT_IDS } from "../../constants/docs";
import { convertTagsToFilter } from "../../utils";

export const SUPPORTED_DOC_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx"];

// Best-effort file extension for a doc (from fileType, the S3 Key, or location).
export const getDocExtension = (doc) => {
    if (!doc) return "";
    if (doc.fileType) return String(doc.fileType).split(".").pop().toLowerCase();
    return getFileExtension(doc.file?.Key || doc.file);
};

// System tags come from the backend's system groups (Projects / Project
// objects / File type); they carry `systemKey` and a `system:*` groupId.
const isSystemDocTag = (tag) => Boolean(tag?.systemKey) || String(tag?.groupId || "").startsWith("system:");

// Which slice a doc tag belongs to: system | filter | attribute. Untyped
// non-system tags default to filter.
export const docTagKind = (tag) => (isSystemDocTag(tag) ? "system" : (tag?.type === "attribute" ? "attribute" : "filter"));

const toMetadataItem = (tag, kind) => ({
    displayName: tag.displayName,
    tooltipContent: tag.group || "",
    tooltipEnabled: Boolean(tag.group),
    id: tag.valueId || tag.id,
    className: `doc-meta-item doc-meta-${kind}`,
});

// Map a document's filter and attribute values into ListItem `items` for display.
// Shared across the docs list and the docs management pages.
export const getMetadataItems = (docData = {}) => {
    const tags = docData?.tags;
    if (!Array.isArray(tags)) return [];
    const byKind = { filter: [], attribute: [] };
    tags.forEach((tag) => {
        const kind = docTagKind(tag);
        if (byKind[kind]) byKind[kind].push(toMetadataItem(tag, kind));
    });
    return [...byKind.filter, ...byKind.attribute];
};

// An annex/child doc carries `parentDocId` (the backend's field for the base file).
export const isAnnexDoc = (doc) => Boolean(doc?.parentDocId);

// The id of a document's parent (base file), if it is an annex/child.
export const getParentId = (doc) => doc?.parentDocId || null;

export const isOutdatedSelectionError = (error) => error?.response?.status === 409;

export const OUTDATED_SELECTION_MESSAGE = "The list changed since you selected it. It has been refreshed, please select again.";

// Flatten parents and their inline `children` (annexes) into a single ordered
// list, so the docs list can show annexes indented right under their parent.
export const flattenDocsWithChildren = (docs = []) =>
    docs.flatMap((doc) => [doc, ...(doc.children || [])]);

// Sort options for the docs lists. `id`s are the backend `sort` values
// (field asc / -field desc) sent verbatim to GET /docs & POST /docs/search.
export const DOC_SORT_OPTIONS = [
    { id: "-createdAt", label: "Newest first" },
    { id: "createdAt", label: "Older first", withHr: true },
    { id: "displayName", label: "A - Z" },
    { id: "-displayName", label: "Z - A" },
];

export const hasNonDefaultDocFilters = (filterValue = {}, defaultFilterValue = {}) => {
    const isEmpty = (value) => value == null || value === "" || (Array.isArray(value) && value.length === 0);
    const normalize = (value) => (Array.isArray(value) ? [...value].sort() : value);
    const keys = new Set([...Object.keys(filterValue), ...Object.keys(defaultFilterValue)]);
    for (const key of keys) {
        const current = filterValue[key];
        const defaultValue = defaultFilterValue[key];
        if (isEmpty(current) && isEmpty(defaultValue)) continue;
        if (JSON.stringify(normalize(current)) !== JSON.stringify(normalize(defaultValue))) return true;
    }
    return false;
};

// Everything outside these keys is a dynamic tag group.
const STATIC_DOC_FILTER_KEYS = [DOC_FILES_FIELD_ID, "displayName", "documentNo"];

const hasFilesSelectionChanged = (prevFilterValue = {}, nextFilterValue = {}) => {
    const normalize = (value) => [...(value?.[DOC_FILES_FIELD_ID] || [])].sort().join(",");
    return normalize(prevFilterValue) !== normalize(nextFilterValue);
};

const resetDynamicDocFilters = (filterValue = {}) =>
    STATIC_DOC_FILTER_KEYS.reduce((acc, key) => (
        filterValue[key] !== undefined ? { ...acc, [key]: filterValue[key] } : acc
    ), {});

// A changed Files selection changes which facets exist, so the carried-over
// tag-group selections are dropped (the static name/number searches stay).
export const applyFilesSelectionReset = (prevFilterValue, nextFilterValue) => (
    hasFilesSelectionChanged(prevFilterValue, nextFilterValue)
        ? resetDynamicDocFilters(nextFilterValue)
        : nextFilterValue
);

// The docs filter value for a past chat's saved filters, Files scope included.
// The backend saves the title and number searches as one-value lists, while the
// docs search takes them as text.
export const toRestoredDocFilterValue = (tags, defaultFilterValue) => {
    const { displayName, documentNo, ...tagFilters } = convertTagsToFilter(tags);
    return {
        ...defaultFilterValue,
        ...tagFilters,
        displayName: displayName?.[0] || '',
        documentNo: documentNo?.[0] || '',
    };
};

// Sort options of the project docs list — the placement orderings on top, then
// the field sorts. All are backend `sort` values sent verbatim.
export const PROJECT_DOC_SORT_OPTIONS = [
    { id: DOC_PLACEMENT_SORT_IDS.ORGANIZATION_FIRST, label: "Organization first" },
    { id: DOC_PLACEMENT_SORT_IDS.PROJECT_FIRST, label: "Project first" },
    { id: DOC_PLACEMENT_SORT_IDS.ELEMENT_FIRST, label: "Element first", withHr: true },
    ...DOC_SORT_OPTIONS,
];
